import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type IconName } from '@sg/garnish';

/**
 * Converts a payment type into a label and an icon.
 */
export const useLabelAndIconByType = (): ((
  type: 'card' | 'apple_pay' | 'google_pay' | string,
) => PaymentTypeConversion) => {
  const { formatMessage } = useIntl();

  return useCallback(
    (type: string) => {
      switch (type) {
        case 'card': {
          return {
            label: '',
            icon: 'IconCreditCard',
          };
        }

        case 'apple_pay': {
          return {
            label: formatMessage(messages.applePay),
            icon: 'IconApplePayPayment',
          };
        }

        case 'google_pay': {
          return {
            label: formatMessage(messages.googlePay),
            icon: 'IconGooglePay',
          };
        }

        default: {
          return {
            label: formatMessage(messages.paymentMethod),
            icon: 'IconCreditCard',
          };
        }
      }
    },
    [formatMessage],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  applePay: {
    defaultMessage: 'Apple Pay',
    description: 'Stripe Payment | Apple Pay',
  },
  googlePay: {
    defaultMessage: 'Google Pay',
    description: 'Stripe Payment | Google Pay',
  },
  paymentMethod: {
    defaultMessage: 'Payment Method',
    description: 'Stripe Payment | Payment Method',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentTypeConversion = {
  label: string;
  icon: IconName;
};
